(() => {
  const ROOT_SELECTOR = '[data-hr-spirit-bundle-buybox]';
  const FLAVORS = [
    { key: 'B', name: 'Berry Whip' },
    { key: 'S', name: 'Strawberry Shortcake' },
    { key: 'P', name: 'Peach Cobbler' }
  ];
  const FLAVOR_INDEX = Object.fromEntries(FLAVORS.map((flavor, index) => [flavor.key, index]));
  const SKU_FLAVOR_KEYS = { BER: 'B', STR: 'S', PEA: 'P' };
  const DEFAULT_MIXES = {
    1: { B: 0, S: 0, P: 1 },
    2: { B: 1, S: 0, P: 1 },
    3: { B: 1, S: 1, P: 1 }
  };
  const THC_PER_BOTTLE_MG = 1270;
  const MINIMUM_THREE_PLUS = 3;
  const MAXIMUM_BOTTLES = 6;
  const DRAWER_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  const parseConfig = (root) => {
    const node = root.querySelector('[data-hr-spirit-bundle-config]')
      || root.closest('[data-hr-product-spirit-owned]')?.querySelector('[data-hr-spirit-bundle-config]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('High Rider: invalid Spirit bundle configuration.', error);
      return null;
    }
  };

  const canonicalMix = (value) => [...String(value || '').toUpperCase()]
    .filter((key) => Object.prototype.hasOwnProperty.call(FLAVOR_INDEX, key))
    .sort((left, right) => FLAVOR_INDEX[left] - FLAVOR_INDEX[right])
    .join('');

  const variantKey = (variant) => {
    const sku = String(variant?.sku || '').trim().toUpperCase();
    const legacyMatch = sku.match(/-(1|2|3)-([BSP]+)$/);
    const currentMatch = sku.match(/-((?:BER|STR|PEA)(?:-(?:BER|STR|PEA)){0,2})-([123])$/);
    if (!legacyMatch && !currentMatch) return null;
    const size = Number(legacyMatch?.[1] || currentMatch[2]);
    const mix = canonicalMix(legacyMatch
      ? legacyMatch[2]
      : currentMatch[1].split('-').map((token) => SKU_FLAVOR_KEYS[token]).join(''));
    return mix.length === size ? `${size}:${mix}` : null;
  };

  const addOnFlavor = (variant) => {
    const sku = String(variant?.sku || '').trim().toUpperCase();
    const legacyMatch = sku.match(/-ADD-([BSP])$/);
    if (legacyMatch && Object.prototype.hasOwnProperty.call(FLAVOR_INDEX, legacyMatch[1])) return legacyMatch[1];
    const currentMatch = sku.match(/-(BER|STR|PEA)-ADD$/);
    return currentMatch ? SKU_FLAVOR_KEYS[currentMatch[1]] : null;
  };

  const money = (cents, format = '${{amount}}') => {
    const value = Number(cents || 0);
    const formatNumber = (precision, thousands, decimal) => {
      const [whole, fraction] = (value / 100).toFixed(precision).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    const placeholder = format.match(/\{\{\s*(amount(?:_no_decimals)?(?:_with_comma_separator|_with_space_separator)?)\s*\}\}/)?.[1];
    const values = {
      amount: formatNumber(2, ',', '.'),
      amount_no_decimals: formatNumber(0, ',', '.'),
      amount_with_comma_separator: formatNumber(2, '.', ','),
      amount_no_decimals_with_comma_separator: formatNumber(0, '.', ','),
      amount_with_space_separator: formatNumber(2, ' ', ','),
      amount_no_decimals_with_space_separator: formatNumber(0, ' ', ',')
    };
    return placeholder
      ? format.replace(/\{\{\s*[^}]+\s*\}\}/, values[placeholder] || values.amount)
      : `$${values.amount}`;
  };

  const displayMoney = (cents, format = '${{amount}}') => {
    const floored = Math.floor(Number(cents || 0) / 100) * 100;
    return money(floored, format).replace(/([.,])00(?=[^\d]*$)/, '');
  };

  const naturalList = (items) => {
    if (items.length < 2) return items[0] || '';
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
  };

  const initialize = (root) => {
    if (root.dataset.hrBundleInitialized === 'true') return;
    const config = parseConfig(root);
    if (!config?.variants?.length) return;
    root.dataset.hrBundleInitialized = 'true';

    const offers = [...root.querySelectorAll('[data-hr-bundle-offer]')];
    const tabs = [...root.querySelectorAll('[data-hr-purchase-tab]')];
    const planSelect = root.querySelector('[data-hr-bundle-plan]');
    const planLabel = root.querySelector('.hr-spirit-bundle__plan-label');
    const subscriptionNote = root.querySelector('[data-hr-subscription-note]');
    const subscriptionUnavailable = root.querySelector('[data-hr-subscription-unavailable]');
    const picker = root.querySelector('[data-hr-spirit-mix-match]');
    const pickerSlot = root.querySelector('[data-hr-bundle-picker-slot]');
    const expansionPanel = root.querySelector('.hr-spirit-bundle__expansion');
    const helper = root.querySelector('[data-hr-bundle-helper]');
    const errorNode = root.querySelector('[data-hr-bundle-error]');
    const atc = root.querySelector('[data-hr-bundle-atc]');
    const atcLabel = root.querySelector('[data-hr-bundle-atc-label]');
    const loadingIndicator = root.querySelector('[data-hr-bundle-loading]');
    const totalSummary = root.querySelector('[data-hr-bundle-total]');
    const packPreview = picker?.querySelector('[data-hr-pack-preview]');
    const packBottles = picker?.querySelector('[data-hr-pack-bottles]');
    const packSummary = picker?.querySelector('[data-hr-pack-summary]');
    const mixStatus = picker?.querySelector('[data-hr-mix-status]');
    const mixMessage = picker?.querySelector('[data-hr-mix-message]');
    if (offers.length !== 3 || !picker || !atc || !atcLabel) return;

    const unavailableFlavorKeys = new Set(
      String(picker.dataset.hrUnavailableFlavors || '')
        .split(',')
        .map((key) => key.trim().toUpperCase())
        .filter((key) => Object.prototype.hasOwnProperty.call(FLAVOR_INDEX, key))
    );
    const flavorIsUnavailable = (flavorKey) => unavailableFlavorKeys.has(flavorKey);
    const variantsByMix = new Map();
    const addOnsByFlavor = new Map();
    config.variants.forEach((variant) => {
      const key = variantKey(variant);
      const flavor = addOnFlavor(variant);
      if (key && !variantsByMix.has(key)) variantsByMix.set(key, variant);
      if (flavor && !addOnsByFlavor.has(flavor)) addOnsByFlavor.set(flavor, variant);
    });

    const selectedMixes = Object.fromEntries(
      Object.entries(DEFAULT_MIXES).map(([size, counts]) => [size, { ...counts }])
    );
    Object.values(selectedMixes).forEach((counts) => {
      let displaced = 0;
      FLAVORS.forEach((flavor) => {
        if (!flavorIsUnavailable(flavor.key)) return;
        displaced += Number(counts[flavor.key] || 0);
        counts[flavor.key] = 0;
      });
      if (!displaced) return;
      const fallback = FLAVORS.find((flavor) => (
        flavor.key === 'P' && !flavorIsUnavailable(flavor.key)
      )) || FLAVORS.find((flavor) => (
        !flavorIsUnavailable(flavor.key) && Number(counts[flavor.key] || 0) > 0
      )) || FLAVORS.find((flavor) => !flavorIsUnavailable(flavor.key));
      if (fallback) counts[fallback.key] = Number(counts[fallback.key] || 0) + displaced;
    });
    let selectedOfferIndex = 2;
    let purchaseMode = 'one-time';
    let selectedPlan = null;
    let loading = false;
    let lastPreviewSignature = '';
    if (expansionPanel && !expansionPanel.id) expansionPanel.id = `${root.id || 'HrSpiritBundle'}-Expansion`;

    const countsFor = (index = selectedOfferIndex) => selectedMixes[index + 1];
    const isThreePlus = (index = selectedOfferIndex) => index === 2;
    const minimumFor = (index = selectedOfferIndex) => index + 1;
    const totalFor = (counts = countsFor()) => FLAVORS.reduce(
      (total, flavor) => total + Number(counts?.[flavor.key] || 0),
      0
    );
    const mixFor = (counts) => FLAVORS.map(
      (flavor) => flavor.key.repeat(Number(counts?.[flavor.key] || 0))
    ).join('');

    const baseCountsFor = (counts, index = selectedOfferIndex) => {
      if (!isThreePlus(index)) return { ...counts };
      const base = Object.fromEntries(FLAVORS.map((flavor) => [flavor.key, 0]));
      let remaining = MINIMUM_THREE_PLUS;
      FLAVORS.forEach((flavor) => {
        if (!remaining || Number(counts?.[flavor.key] || 0) < 1) return;
        base[flavor.key] = 1;
        remaining -= 1;
      });
      while (remaining > 0) {
        const next = FLAVORS
          .filter((flavor) => Number(counts?.[flavor.key] || 0) > base[flavor.key])
          .sort((left, right) => {
            const leftRemaining = Number(counts[left.key] || 0) - base[left.key];
            const rightRemaining = Number(counts[right.key] || 0) - base[right.key];
            return rightRemaining - leftRemaining || FLAVOR_INDEX[left.key] - FLAVOR_INDEX[right.key];
          })[0];
        if (!next) return null;
        base[next.key] += 1;
        remaining -= 1;
      }
      return base;
    };

    const cartPlan = (counts = countsFor(), index = selectedOfferIndex) => {
      if (FLAVORS.some((flavor) => (
        flavorIsUnavailable(flavor.key) && Number(counts?.[flavor.key] || 0) > 0
      ))) return null;
      const total = totalFor(counts);
      const minimum = minimumFor(index);
      const maximum = isThreePlus(index) ? MAXIMUM_BOTTLES : minimum;
      if (total < minimum || total > maximum || (!isThreePlus(index) && total !== minimum)) return null;
      const baseCounts = baseCountsFor(counts, index);
      if (!baseCounts) return null;
      const baseSize = isThreePlus(index) ? MINIMUM_THREE_PLUS : minimum;
      const baseVariant = variantsByMix.get(`${baseSize}:${mixFor(baseCounts)}`) || null;
      if (!baseVariant) return null;
      const items = [{ variant: baseVariant, quantity: 1, role: 'base' }];
      if (isThreePlus(index)) {
        FLAVORS.forEach((flavor) => {
          const quantity = Number(counts[flavor.key] || 0) - Number(baseCounts[flavor.key] || 0);
          if (quantity > 0) items.push({
            variant: addOnsByFlavor.get(flavor.key) || null,
            quantity,
            role: 'add-on',
            flavor
          });
        });
      }
      const complete = items.every((item) => item.variant);
      return {
        baseVariant,
        items,
        total,
        complete,
        available: complete && items.every((item) => item.variant.available)
      };
    };

    const planForVariant = (variant, plan = selectedPlan) => {
      if (!variant || !plan) return null;
      return variant.sellingPlans?.find((candidate) => Number(candidate.id) === Number(plan.id))
        || variant.sellingPlans?.find((candidate) => String(candidate.name) === String(plan.name))
        || null;
    };

    const commonPlans = (plan = cartPlan()) => {
      if (!plan?.complete) return [];
      return (plan.baseVariant.sellingPlans || []).filter((candidate) => (
        plan.items.every((item) => planForVariant(item.variant, candidate))
      ));
    };

    const pricedPlan = (plan = cartPlan(), mode = purchaseMode, selected = selectedPlan) => {
      if (!plan?.complete) return null;
      const lines = plan.items.map((item) => {
        const allocation = mode === 'subscription' ? planForVariant(item.variant, selected) : null;
        return {
          ...item,
          allocation,
          unitPrice: mode === 'subscription' ? Number(allocation?.price) : Number(item.variant.price)
        };
      });
      if (lines.some((line) => !Number.isFinite(line.unitPrice))) return null;
      return {
        ...plan,
        lines,
        price: lines.reduce((sum, line) => sum + line.unitPrice * Number(line.quantity), 0),
        oneTimePrice: plan.items.reduce(
          (sum, item) => sum + Number(item.variant.price) * Number(item.quantity),
          0
        )
      };
    };

    const replacementFlavorFor = (counts, target) => FLAVORS
      .filter((flavor) => (
        !flavorIsUnavailable(flavor.key) &&
        flavor.key !== target &&
        Number(counts[flavor.key] || 0) > 0
      ))
      .sort((left, right) => {
        const difference = Number(counts[right.key] || 0) - Number(counts[left.key] || 0);
        return difference || FLAVOR_INDEX[right.key] - FLAVOR_INDEX[left.key];
      })[0] || null;

    const nextIncrement = (counts, flavorKey, index = selectedOfferIndex) => {
      if (flavorIsUnavailable(flavorKey)) return null;
      const next = { ...counts };
      const total = totalFor(counts);
      if (isThreePlus(index)) {
        if (total >= MAXIMUM_BOTTLES) return null;
        next[flavorKey] += 1;
        return next;
      }
      const maximum = minimumFor(index);
      if (total < maximum) {
        next[flavorKey] += 1;
        return next;
      }
      const replacement = replacementFlavorFor(counts, flavorKey);
      if (!replacement) return null;
      next[replacement.key] -= 1;
      next[flavorKey] += 1;
      return next;
    };

    const bottleAssets = packPreview ? {
      B: { src: packPreview.dataset.hrBerryBottleSrc, width: 237, height: 600 },
      S: { src: packPreview.dataset.hrStrawberryBottleSrc, width: 237, height: 600 },
      P: { src: packPreview.dataset.hrPeachBottleSrc, width: 237, height: 600 }
    } : {};

    const renderPackPreview = (counts, index = selectedOfferIndex) => {
      if (!packPreview || !packBottles || !packSummary) return;
      const total = totalFor(counts);
      const groups = FLAVORS.flatMap((flavor) => {
        const quantity = Number(counts[flavor.key] || 0);
        return quantity ? [{ flavor, quantity }] : [];
      });
      const signature = groups.map(({ flavor, quantity }) => `${flavor.key}:${quantity}`).join('|');
      if (signature !== lastPreviewSignature) {
        const fragment = document.createDocumentFragment();
        groups.forEach(({ flavor, quantity }) => {
          const item = document.createElement('div');
          const visual = document.createElement('div');
          const label = document.createElement('span');
          item.className = 'hr-spirit-pack-preview__bottle';
          item.dataset.flavor = flavor.key;
          item.dataset.quantity = String(quantity);
          visual.className = 'hr-spirit-pack-preview__visual';
          visual.style.setProperty('--hr-stack-marker-offset', `${((quantity - 1) / 2) * 22 - 3}px`);
          visual.style.setProperty('--hr-stack-marker-offset-mobile', `${((quantity - 1) / 2) * 18 - 3}px`);
          label.className = 'hr-spirit-pack-preview__bottle-name';
          for (let bottleIndex = 0; bottleIndex < quantity; bottleIndex += 1) {
            const image = document.createElement('img');
            const asset = bottleAssets[flavor.key] || bottleAssets.S;
            const centeredIndex = bottleIndex - ((quantity - 1) / 2);
            image.className = 'hr-spirit-pack-preview__image hr-spirit-pack-preview__image--real';
            image.src = asset.src;
            image.alt = '';
            image.width = asset.width;
            image.height = asset.height;
            image.decoding = 'async';
            image.draggable = false;
            image.style.setProperty('--hr-stack-offset', `${centeredIndex * 22}px`);
            image.style.setProperty('--hr-stack-offset-mobile', `${centeredIndex * 18}px`);
            image.style.zIndex = String(bottleIndex + 1);
            visual.appendChild(image);
          }
          const markerSource = picker.querySelector(
            `[data-hr-flavor-row="${flavor.key}"] .hr-spirit-mix-match__swatch`
          );
          if (markerSource) {
            const marker = document.createElement('span');
            marker.className = 'hr-spirit-pack-preview__marker';
            marker.appendChild(markerSource.cloneNode(true));
            visual.appendChild(marker);
          }
          const name = document.createElement('span');
          name.textContent = flavor.name;
          label.appendChild(name);
          if (quantity > 1) {
            const count = document.createElement('span');
            count.className = 'hr-spirit-pack-preview__bottle-count';
            count.textContent = `×${quantity}`;
            label.appendChild(count);
          }
          item.append(visual, label);
          fragment.appendChild(item);
        });
        packBottles.replaceChildren(fragment);
        lastPreviewSignature = signature;
      }
      packPreview.dataset.packSize = String(total);
      packPreview.dataset.flavorGroups = String(groups.length);
      const descriptions = groups.map(({ flavor, quantity }) => (
        quantity > 1 ? `${flavor.name} ×${quantity}` : flavor.name
      ));
      packSummary.textContent = `Your pack: ${naturalList(descriptions)}.`;
    };

    const setText = (scope, selector, value) => {
      const node = scope?.querySelector(selector);
      if (node) node.textContent = value;
    };

    const offerDisplay = (index) => {
      const counts = countsFor(index);
      const plan = cartPlan(counts, index);
      let planChoice = selectedPlan;
      if (purchaseMode === 'subscription') {
        const options = commonPlans(plan);
        planChoice = options.find((candidate) => Number(candidate.id) === Number(selectedPlan?.id))
          || options.find((candidate) => candidate.name === selectedPlan?.name)
          || options[0]
          || null;
      }
      const priced = pricedPlan(plan, purchaseMode, planChoice);
      if (!priced) return { plan, priced: null };
      const count = plan.total;
      const singleBottleVariant = variantsByMix.get('1:B') || variantsByMix.get('1:S') || variantsByMix.get('1:P');
      const retailReference = Number(singleBottleVariant?.price || 0) * count;
      const compare = purchaseMode === 'subscription' ? priced.oneTimePrice : retailReference;
      const displaySavings = Math.max(
        0,
        (Math.floor(compare / 100) - Math.floor(priced.price / 100)) * 100
      );
      return {
        plan,
        priced,
        count,
        dosage: count * THC_PER_BOTTLE_MG,
        centsPerMg: priced.price / (count * THC_PER_BOTTLE_MG),
        compare,
        displaySavings,
        planChoice
      };
    };

    const syncPlanSelect = (plan = cartPlan()) => {
      if (!planSelect) return;
      const options = commonPlans(plan);
      if (purchaseMode === 'subscription' && options.length) {
        const nextSelected = options.find((candidate) => Number(candidate.id) === Number(selectedPlan?.id))
          || options.find((candidate) => candidate.name === selectedPlan?.name)
          || options[0];
        selectedPlan = nextSelected;
      }
      planSelect.replaceChildren(...options.map((planOption) => {
        const option = document.createElement('option');
        option.value = String(planOption.id);
        option.textContent = planOption.name;
        option.selected = Number(planOption.id) === Number(selectedPlan?.id);
        return option;
      }));
      const hidden = purchaseMode !== 'subscription' || !options.length;
      planSelect.disabled = hidden || loading;
      planSelect.closest('[data-hr-bundle-plan-wrap]')?.toggleAttribute('hidden', hidden);
      planLabel?.toggleAttribute('hidden', hidden);
    };

    const render = () => {
      const plan = cartPlan();
      const subscriptionPlans = commonPlans(plan);
      const subscriptionAvailable = subscriptionPlans.length > 0;
      syncPlanSelect(plan);
      root.dataset.hrPurchaseMode = purchaseMode;
      root.dataset.hrBundlePack = isThreePlus() ? '3+' : String(minimumFor());

      tabs.forEach((tab) => {
        const mode = tab.dataset.hrPurchaseTab;
        const selected = mode === purchaseMode;
        const unavailable = mode === 'subscription' && !subscriptionAvailable;
        tab.setAttribute('aria-selected', String(selected));
        tab.setAttribute('aria-disabled', String(loading));
        tab.tabIndex = selected ? 0 : -1;
        tab.toggleAttribute('disabled', loading);
        tab.dataset.selected = String(selected);
        tab.title = unavailable ? 'Subscription checkout is temporarily unavailable for this bundle.' : '';
      });
      root.querySelectorAll('[data-hr-purchase-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.hrPurchasePanel !== purchaseMode;
      });
      if (subscriptionNote) {
        subscriptionNote.hidden = purchaseMode !== 'subscription';
        subscriptionNote.textContent = subscriptionAvailable
          ? 'Subscribe and save on every delivery. Skip or cancel anytime.'
          : 'Subscription options are temporarily unavailable for this bundle.';
      }
      if (subscriptionUnavailable) {
        subscriptionUnavailable.hidden = subscriptionAvailable || purchaseMode === 'subscription';
      }

      offers.forEach((offer, index) => {
        const selected = index === selectedOfferIndex;
        const display = offerDisplay(index);
        const count = display.count || totalFor(countsFor(index));
        offer.setAttribute('aria-checked', String(selected));
        offer.setAttribute('aria-expanded', String(selected));
        if (expansionPanel?.id) offer.setAttribute('aria-controls', expansionPanel.id);
        offer.tabIndex = selected ? 0 : -1;
        offer.dataset.selected = String(selected);
        offer.dataset.available = String(Boolean(display.plan?.available && display.priced));
        setText(offer, '[data-hr-offer-title]', index === 2 ? '3+ BOTTLES' : `${index + 1} ${index ? 'BOTTLES' : 'BOTTLE'}`);
        if (display.priced) {
          setText(offer, '[data-hr-offer-dosage]', `${display.dosage.toLocaleString('en-US')}MG THC • ${display.centsPerMg.toFixed(1)}¢ / MG`);
          setText(offer, '[data-hr-offer-price]', displayMoney(display.priced.price, config.moneyFormat));
          setText(offer, '[data-hr-offer-compare]', display.compare > display.priced.price
            ? displayMoney(display.compare, config.moneyFormat)
            : '');
          setText(offer, '[data-hr-offer-savings]', display.displaySavings > 0
            ? `Save ${displayMoney(display.displaySavings, config.moneyFormat)}`
            : '');
        } else {
          setText(offer, '[data-hr-offer-price]', 'Unavailable');
          setText(offer, '[data-hr-offer-compare]', '');
          setText(offer, '[data-hr-offer-savings]', '');
        }
        setText(offer, '[data-hr-offer-benefits]', index === 0
          ? '30 Day Guarantee'
          : 'Free Shipping · 30 Day Guarantee');
      });

      const selectedOffer = offers[selectedOfferIndex];
      if (expansionPanel && selectedOffer.parentElement?.nextElementSibling !== expansionPanel) {
        selectedOffer.parentElement?.after(expansionPanel);
      }
      const expansion = expansionPanel?.querySelector('[data-hr-bundle-picker-slot]') || pickerSlot;
      if (expansion && picker.parentElement !== expansion) expansion.appendChild(picker);
      picker.hidden = false;
      picker.dataset.packSize = isThreePlus() ? '3+' : String(minimumFor());

      const counts = countsFor();
      const total = totalFor(counts);
      const minimum = minimumFor();
      const display = offerDisplay(selectedOfferIndex);
      renderPackPreview(counts);
      if (mixStatus) mixStatus.textContent = isThreePlus()
        ? `${total} selected · max ${MAXIMUM_BOTTLES}`
        : `${total} of ${minimum} selected`;

      FLAVORS.forEach((flavor) => {
        const quantity = Number(counts[flavor.key] || 0);
        const flavorUnavailable = flavorIsUnavailable(flavor.key);
        const row = picker.querySelector(`[data-hr-flavor-row="${flavor.key}"]`);
        const counter = picker.querySelector(`[data-hr-flavor-counter="${flavor.key}"]`);
        const output = picker.querySelector(`[data-hr-flavor-quantity="${flavor.key}"]`);
        const unavailableLabel = row?.querySelector('[data-hr-flavor-unavailable]');
        const decrement = picker.querySelector(`[data-hr-flavor-action="decrement"][data-hr-flavor="${flavor.key}"]`);
        const increment = picker.querySelector(`[data-hr-flavor-action="increment"][data-hr-flavor="${flavor.key}"]`);
        const next = nextIncrement(counts, flavor.key);
        const nextPlan = next ? cartPlan(next) : null;
        const nextSubscriptionPlans = nextPlan ? commonPlans(nextPlan) : [];
        const incrementAvailable = Boolean(nextPlan?.available) && (
          purchaseMode !== 'subscription' || nextSubscriptionPlans.some((candidate) => (
            Number(candidate.id) === Number(selectedPlan?.id) || candidate.name === selectedPlan?.name
          ))
        );
        if (row) {
          row.dataset.selected = String(quantity > 0);
          row.dataset.unavailable = String(flavorUnavailable);
        }
        if (unavailableLabel) unavailableLabel.hidden = !flavorUnavailable;
        if (counter) counter.setAttribute(
          'aria-label',
          `${flavor.name} quantity: ${quantity}${flavorUnavailable ? ', sold out' : ''}`
        );
        if (output) output.textContent = String(quantity);
        if (decrement) {
          const atMinimum = isThreePlus() && total <= MINIMUM_THREE_PLUS;
          decrement.disabled = flavorUnavailable || loading || quantity === 0 || atMinimum;
          decrement.setAttribute('aria-disabled', String(decrement.disabled));
          decrement.setAttribute('aria-label', flavorUnavailable
            ? `${flavor.name} is sold out`
            : (atMinimum
              ? `Minimum ${MINIMUM_THREE_PLUS} bottles selected`
              : `Remove one ${flavor.name} bottle`));
          decrement.title = flavorUnavailable ? 'Sold out' : '';
        }
        if (increment) {
          increment.disabled = flavorUnavailable || loading || !next || !incrementAvailable;
          increment.setAttribute('aria-disabled', String(increment.disabled));
          let label = `Add one ${flavor.name} bottle`;
          if (flavorUnavailable) label = `${flavor.name} is sold out`;
          else if (isThreePlus() && total >= MAXIMUM_BOTTLES) label = `Maximum ${MAXIMUM_BOTTLES} bottles selected`;
          else if (!next || !incrementAvailable) label = `${flavor.name} is unavailable for this selection`;
          else if (!isThreePlus() && total >= minimum) {
            const replacement = replacementFlavorFor(counts, flavor.key);
            if (replacement) label = `Replace one ${replacement.name} bottle with ${flavor.name}`;
          }
          increment.setAttribute('aria-label', label);
          increment.title = flavorUnavailable ? 'Sold out' : '';
        }
      });

      const extraPrices = FLAVORS.flatMap((flavor) => {
        const variant = addOnsByFlavor.get(flavor.key);
        const allocation = purchaseMode === 'subscription' ? planForVariant(variant) : null;
        const price = purchaseMode === 'subscription' ? allocation?.price : variant?.price;
        return Number.isFinite(Number(price)) && Number(price) > 0 ? [Number(price)] : [];
      });
      const extraPrice = extraPrices[0] || null;
      if (helper) {
        const helperText = helper.querySelector(':scope > span:last-child') || helper;
        helperText.textContent = isThreePlus()
          ? (total >= MAXIMUM_BOTTLES
            ? `Maximum of ${MAXIMUM_BOTTLES} bottles selected.`
            : (extraPrice
              ? `Add more bottles for ${displayMoney(extraPrice, config.moneyFormat)} each.`
              : 'Additional bottles are unavailable for this subscription.'))
          : 'Tap + on any flavor to swap your selection.';
      }
      if (totalSummary) {
        const totalValue = totalSummary.querySelector('.hr-spirit-bundle__total-value') || totalSummary;
        totalValue.textContent = `${total} ${total === 1 ? 'bottle' : 'bottles'} · ${purchaseMode === 'subscription' ? 'subscription' : 'one-time'}`;
      }
      if (mixMessage) mixMessage.hidden = true;

      let issue = '';
      let subscriptionUnavailableForSelection = false;
      if (!plan?.complete) issue = 'This flavor combination is unavailable.';
      else if (!plan.available) issue = 'This flavor combination is currently sold out.';
      else if (purchaseMode === 'subscription' && !display.planChoice) {
        subscriptionUnavailableForSelection = true;
        issue = 'Subscription unavailable';
      }
      if (errorNode && !errorNode.dataset.commerceError) {
        const alertIssue = subscriptionUnavailableForSelection ? '' : issue;
        errorNode.textContent = alertIssue;
        errorNode.hidden = !alertIssue;
      }

      const canAdd = Boolean(display.priced && plan?.available && !loading && !issue);
      atc.disabled = !canAdd;
      atc.setAttribute('aria-disabled', String(!canAdd));
      atc.setAttribute('aria-busy', String(loading));
      root.dataset.loading = String(loading);
      offers.forEach((offer) => offer.setAttribute('aria-disabled', String(loading)));
      if (loading) atcLabel.textContent = 'Adding your bundle…';
      else if (!canAdd) atcLabel.textContent = subscriptionUnavailableForSelection
        ? 'Subscription unavailable'
        : (issue || 'Unavailable');
      else atcLabel.textContent = purchaseMode === 'subscription'
        ? `Subscribe — ${displayMoney(display.priced.price, config.moneyFormat)}`
        : `Add ${display.count} ${display.count === 1 ? 'Bottle' : 'Bottles'} — ${displayMoney(display.priced.price, config.moneyFormat)}`;
      atcLabel.hidden = loading;
      if (loadingIndicator) loadingIndicator.hidden = !loading;
    };

    const clearCommerceError = () => {
      if (!errorNode) return;
      delete errorNode.dataset.commerceError;
      errorNode.textContent = '';
      errorNode.hidden = true;
    };

    const chooseOffer = (index) => {
      if (loading || !offers[index]) return;
      selectedOfferIndex = index;
      clearCommerceError();
      render();
    };

    offers.forEach((offer, index) => {
      offer.addEventListener('click', (event) => {
        if (event.target instanceof Element && event.target.closest('[data-hr-spirit-mix-match], select, input, a')) return;
        chooseOffer(index);
      });
      offer.addEventListener('keydown', (event) => {
        if (loading) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          chooseOffer(index);
          return;
        }
        const keys = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') selectedOfferIndex = 0;
        else if (event.key === 'End') selectedOfferIndex = offers.length - 1;
        else {
          const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
          selectedOfferIndex = (index + direction + offers.length) % offers.length;
        }
        clearCommerceError();
        render();
        offers[selectedOfferIndex].focus();
      });
    });

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        if (loading || tab.getAttribute('aria-disabled') === 'true') return;
        purchaseMode = tab.dataset.hrPurchaseTab;
        if (purchaseMode === 'subscription') selectedPlan = commonPlans()[0] || null;
        clearCommerceError();
        render();
      });
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const enabled = tabs.filter((candidate) => candidate.getAttribute('aria-disabled') !== 'true');
        if (!enabled.length) return;
        const current = enabled.indexOf(tab);
        const next = event.key === 'Home' ? enabled[0]
          : event.key === 'End' ? enabled[enabled.length - 1]
            : enabled[(current + (event.key === 'ArrowLeft' ? -1 : 1) + enabled.length) % enabled.length];
        next.focus();
        next.click();
      });
    });

    planSelect?.addEventListener('change', () => {
      const options = commonPlans();
      selectedPlan = options.find((candidate) => Number(candidate.id) === Number(planSelect.value)) || options[0] || null;
      clearCommerceError();
      render();
    });

    picker.addEventListener('click', (event) => {
      const control = event.target instanceof Element ? event.target.closest('[data-hr-flavor-action]') : null;
      if (!control || control.disabled || loading) return;
      event.preventDefault();
      event.stopPropagation();
      const counts = countsFor();
      const flavorKey = control.dataset.hrFlavor;
      if (!Object.prototype.hasOwnProperty.call(counts, flavorKey)) return;
      if (flavorIsUnavailable(flavorKey)) return;
      if (control.dataset.hrFlavorAction === 'decrement') {
        if (counts[flavorKey] > 0 && (!isThreePlus() || totalFor(counts) > MINIMUM_THREE_PLUS)) {
          counts[flavorKey] -= 1;
        }
      } else {
        const next = nextIncrement(counts, flavorKey);
        if (next) FLAVORS.forEach((flavor) => { counts[flavor.key] = next[flavor.key]; });
      }
      clearCommerceError();
      render();
    });

    const cartUrl = (path) => {
      const rootPath = window.Shopify?.routes?.root || '/';
      return `${rootPath}${path}`.replace(/\/+/g, '/');
    };

    const cartRequest = async (url, body, message) => {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        headers: body ? {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        } : { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: body ? JSON.stringify(body) : undefined
      });
      let data;
      try {
        data = await response.json();
      } catch (_) {
        data = null;
      }
      if (!response.ok || data?.status) throw new Error(data?.description || data?.message || message);
      return data;
    };

    const tokenForAdd = () => {
      const random = window.crypto?.getRandomValues
        ? [...window.crypto.getRandomValues(new Uint32Array(2))].map((value) => value.toString(36)).join('')
        : Math.random().toString(36).slice(2);
      return `spirit-${Date.now().toString(36)}-${random}`;
    };

    const validateAddedBundle = (cart, plan, token, mode) => {
      const lines = (cart.items || []).filter((item) => item.properties?._hr_bundle_id === token);
      const expected = new Map(plan.lines.map((line) => [Number(line.variant.id), Number(line.quantity)]));
      const actual = new Map();
      lines.forEach((line) => {
        const id = Number(line.variant_id || line.id);
        actual.set(id, (actual.get(id) || 0) + Number(line.quantity || 0));
      });
      const quantitiesMatch = expected.size === actual.size
        && [...expected].every(([id, quantity]) => actual.get(id) === quantity);
      if (!quantitiesMatch) throw new Error('Your bottle selection could not be verified.');
      const baseLine = lines.find((line) => Number(line.variant_id || line.id) === Number(plan.baseVariant.id));
      const addOnLines = lines.filter((line) => addOnFlavor({ sku: line.sku }));
      if (addOnLines.length && (!baseLine?.key || addOnLines.some((line) => (
        line.parent_relationship?.parent_key !== baseLine.key
      )))) throw new Error('Your additional bottles could not be linked to this bundle.');
      lines.forEach((line) => {
        const expectedLine = plan.lines.find((candidate) => Number(candidate.variant.id) === Number(line.variant_id || line.id));
        const actualPlanId = Number(line.selling_plan_allocation?.selling_plan?.id || 0);
        const expectedPlanId = Number(expectedLine?.allocation?.id || 0);
        if (mode === 'subscription' && actualPlanId !== expectedPlanId) {
          throw new Error('Your subscription frequency could not be verified.');
        }
        if (mode === 'one-time' && actualPlanId) throw new Error('Your one-time purchase could not be verified.');
      });
      return lines;
    };

    const cleanupBundle = async (token) => {
      const cart = await cartRequest(config.routes.cartJson || cartUrl('cart.js'), null, 'Unable to inspect the cart.');
      const updates = {};
      (cart.items || []).filter((item) => item.properties?._hr_bundle_id === token)
        .forEach((item) => { updates[item.key] = 0; });
      if (Object.keys(updates).length) {
        await cartRequest(config.routes.cartUpdate || cartUrl('cart/update.js'), { updates }, 'Unable to clean up the cart.');
      }
    };

    const visible = (element) => {
      if (!element) return false;
      const styles = window.getComputedStyle(element);
      const bounds = element.getBoundingClientRect();
      return styles.display !== 'none' && styles.visibility !== 'hidden' && styles.opacity !== '0'
        && bounds.width > 0 && bounds.height > 0
        && bounds.right > 0 && bounds.bottom > 0
        && bounds.left < window.innerWidth && bounds.top < window.innerHeight;
    };

    const eliteCartOpen = () => [...document.querySelectorAll('#EliteCartContainer, .ec-cart-container, [role="dialog"]')]
      .some((candidate) => {
        const panel = candidate.matches('.ec-cart-container')
          ? candidate
          : candidate.querySelector('.ec-cart-container');
        return visible(panel);
      });

    const hideIncompleteEliteCart = () => {
      if (eliteCartOpen()) return;
      [document.getElementById('EliteCartBackdrop'), document.getElementById('EliteCartContainer')]
        .filter(Boolean)
        .forEach((element) => {
          element.style.setProperty('display', 'none', 'important');
          element.style.setProperty('visibility', 'hidden', 'important');
          element.setAttribute('aria-hidden', 'true');
        });
    };

    const openEliteCart = async () => {
      const deadline = Date.now() + 2500;
      while (Date.now() < deadline) {
        if (eliteCartOpen()) return true;
        if (typeof window.EliteCart?.openDrawerWithDataRefresh === 'function') {
          try {
            await window.EliteCart.openDrawerWithDataRefresh();
            for (let attempt = 0; attempt < 10; attempt += 1) {
              if (eliteCartOpen()) return true;
              await new Promise((resolve) => window.setTimeout(resolve, 50));
            }
            return false;
          } catch (error) {
            console.warn('High Rider: EliteCart could not be opened.', error);
            return false;
          }
        }
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      return eliteCartOpen();
    };

    const requestDrawerSections = async () => {
      const sectionUrl = new URL(window.location.href);
      sectionUrl.hash = '';
      sectionUrl.searchParams.set('sections', DRAWER_SECTIONS.join(','));
      return cartRequest(sectionUrl.toString(), null, 'Unable to load the cart drawer.');
    };

    const openNativeDrawer = async (cart, sections) => {
      const drawer = document.querySelector('cart-drawer');
      if (typeof drawer?.renderContents !== 'function') return false;
      const drawerSections = sections && DRAWER_SECTIONS.every((id) => sections[id])
        ? sections
        : await requestDrawerSections();
      hideIncompleteEliteCart();
      drawer.classList.remove('is-empty');
      drawer.renderContents({ ...cart, id: cart.items?.[0]?.product_id || config.productId, sections: drawerSections });
      for (let attempt = 0; attempt < 10; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 50));
        if (drawer.classList.contains('active') && visible(drawer)) return true;
      }
      return false;
    };

    const addToCart = async () => {
      const plan = pricedPlan();
      if (loading || !plan?.available || (purchaseMode === 'subscription' && !selectedPlan)) return;
      clearCommerceError();
      loading = true;
      render();
      const token = tokenForAdd();
      const mix = mixFor(countsFor());
      const items = plan.lines.map((line) => {
        const item = {
          id: Number(line.variant.id),
          quantity: Number(line.quantity),
          properties: {
            _hr_bundle_id: token,
            _hr_bundle_mix: mix,
            _hr_bundle_size: String(plan.total)
          }
        };
        if (line.role === 'add-on') item.parent_id = Number(plan.baseVariant.id);
        if (purchaseMode === 'subscription') item.selling_plan = Number(line.allocation.id);
        return item;
      });
      let addState;
      let commerceCommitted = false;
      try {
        addState = await cartRequest(config.routes.cartAdd || cartUrl('cart/add.js'), {
          items,
          sections: DRAWER_SECTIONS.join(','),
          sections_url: `${window.location.pathname}${window.location.search}`
        }, 'Unable to add this bundle.');
        const cart = await cartRequest(config.routes.cartJson || cartUrl('cart.js'), null, 'Unable to verify the cart.');
        const addedLines = validateAddedBundle(cart, plan, token, purchaseMode);
        commerceCommitted = true;
        try {
          document.dispatchEvent(new CustomEvent('high-rider:cart-added', {
            detail: {
              productId: config.productId,
              variantId: plan.baseVariant.id,
              variantIds: items.map((item) => item.id),
              items,
              cartData: cart
            }
          }));
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'hr-spirit-bundle-buybox',
              cartData: cart,
              variantId: plan.baseVariant.id
            });
          }
          const opened = await openEliteCart() || await openNativeDrawer(cart, addState?.sections);
          if (!opened) window.location.assign(config.routes.cart || cartUrl('cart'));
        } catch (presentationError) {
          console.error('High Rider: Spirit cart drawer failed.', presentationError);
          window.location.assign(config.routes.cart || cartUrl('cart'));
        }
        root.dataset.hrLastAddedLineCount = String(addedLines.length);
      } catch (error) {
        console.error('High Rider: Spirit bundle add failed.', error);
        let cleanupFailed = false;
        if (!commerceCommitted) {
          try {
            await cleanupBundle(token);
          } catch (cleanupError) {
            cleanupFailed = true;
            console.error('High Rider: Spirit bundle cleanup failed.', cleanupError);
          }
        }
        if (errorNode) {
          errorNode.dataset.commerceError = 'true';
          errorNode.textContent = cleanupFailed
            ? 'We could not verify this bundle. Please remove the new Spirit items from your cart before checkout.'
            : (error?.message || 'Unable to add this bundle. Please try again.');
          errorNode.hidden = false;
        }
      } finally {
        loading = false;
        render();
      }
    };

    atc.addEventListener('click', (event) => {
      event.preventDefault();
      addToCart();
    });

    const initialPlan = cartPlan();
    const initialPlans = commonPlans(initialPlan);
    if (initialPlans.length) {
      selectedPlan = initialPlans[0];
      purchaseMode = 'subscription';
    }
    render();
  };

  const initializeAll = (container = document) => {
    if (container.matches?.(ROOT_SELECTOR)) initialize(container);
    container.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
